import { Link } from 'react-router-dom';

export interface DeveloperProject {
  id: string;
  slug: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  coverUrl?: string | null;
  city?: string | null;
  region?: string | null;
  status: 'PLANNING' | 'UNDER_CONSTRUCTION' | 'READY' | 'SOLD_OUT';
  investmentStatus: 'OPEN_FOR_INVESTMENT' | 'CLOSED' | 'NA';
  totalUnits?: number | null;
  unitsCount?: number;
  isFeatured?: boolean;
}

export const PROJECT_STATUS_LABELS: Record<DeveloperProject['status'], { en: string; ar: string; cls: string }> = {
  PLANNING: { en: 'Planning', ar: 'قيد التخطيط', cls: 'bg-slate-100 text-slate-600' },
  UNDER_CONSTRUCTION: { en: 'Under construction', ar: 'قيد الإنشاء', cls: 'bg-amber-100 text-amber-700' },
  READY: { en: 'Ready', ar: 'جاهز', cls: 'bg-emerald-100 text-emerald-700' },
  SOLD_OUT: { en: 'Sold out', ar: 'مكتمل البيع', cls: 'bg-rose-100 text-rose-700' },
};

export function investmentLabel(s: DeveloperProject['investmentStatus'], isRTL: boolean): string | null {
  if (s === 'OPEN_FOR_INVESTMENT') return isRTL ? 'مفتوح للاستثمار' : 'Open for investment';
  return null;
}

export default function DeveloperProjectCard({ project, isRTL }: { project: DeveloperProject; isRTL: boolean }) {
  const name = isRTL ? (project.nameAr || project.name) : project.name;
  const description = isRTL ? (project.descriptionAr || project.description) : project.description;
  const location = isRTL ? (project.region || project.city) : (project.city || project.region);
  const st = PROJECT_STATUS_LABELS[project.status];
  const invest = investmentLabel(project.investmentStatus, isRTL);

  return (
    <Link
      to={`/developers/project/${project.slug}`}
      className="group block overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--pv-color-accent-soft)] hover:shadow-[0_14px_34px_-14px_rgba(0,77,64,0.28)]"
    >
      <div
        className="h-36 bg-[#004D40] bg-cover bg-center"
        style={project.coverUrl ? { backgroundImage: `url(${project.coverUrl})` } : undefined}
      />
      <div className={`p-4 ${isRTL ? 'text-right' : ''}`}>
        <div className={`mb-2 flex flex-wrap items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${st.cls}`}>{isRTL ? st.ar : st.en}</span>
          {invest && (
            <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-[11px] font-medium text-indigo-700">{invest}</span>
          )}
        </div>
        <h3 className="truncate text-[15px] font-semibold text-slate-900 transition-colors group-hover:text-[#003930]">{name}</h3>
        <div className={`mt-1 flex items-center gap-2.5 text-xs text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {location && <span className="flex min-w-0 items-center gap-1"><i className="ri-map-pin-2-line" /><span className="truncate">{location}</span></span>}
          {typeof project.unitsCount === 'number' && (
            <span className="flex items-center gap-1"><i className="ri-home-4-line" />{project.unitsCount} {isRTL ? 'وحدة' : 'units'}</span>
          )}
        </div>
        {description && <p className="mt-2 line-clamp-2 text-xs text-slate-500">{description}</p>}
      </div>
    </Link>
  );
}
